import React, { useState, useEffect, useMemo } from "react";
import classNames from "classnames";
import { Label, Input, FormGroup as FormGroupCheckbox } from "reactstrap";
import { useGetInventoryDetail, useUpdateInventory } from "../../../api/inventory/service/index";
import { useParams } from "react-router-dom";
import { useGetEquipmentOptions } from "api/equipment/service";
import { getTheme2FormFieldsCss } from "utils/theme2MultiSelect";
import { useTranslation } from 'react-i18next';

// Helper to deeply clone options object
function deepClone(obj) {
  return JSON.parse(JSON.stringify(obj));
}

function formatTypeTitle(type) {
  if (!type) return "";
  return type
    .replace(/_/g, " ")
    .replace(/\b\w/g, l => l.toUpperCase());
}

const OptionsTab = ({ inventoryId, isDisabled = false, isTheme2 = false }) => {
  const { t } = useTranslation();
  const theme2FieldsCss = useMemo(
    () =>
      isTheme2
        ? getTheme2FormFieldsCss({
            scopeSelector: ".options-tab.options-tab--theme2",
            fieldsSelector: ".options-tab-fields",
          })
        : "",
    [isTheme2]
  );
  const inputStyle = isTheme2
    ? { fontSize: "14px" }
    : { borderRadius: 0, fontSize: "14px" };
  const checkboxStyle = isTheme2
    ? { fontSize: "14px" }
    : { borderRadius: 0, fontSize: "14px" };
  const { id: urlId } = useParams(); // URL'den ID'yi al
  const finalInventoryId = inventoryId || urlId; // Önce prop'u, sonra URL'den gelen ID'yi kullan

  const [formData, setFormData] = useState({
    options: {},
    additional_options: '',
    seller_notes: ''
  });

  const { data: inventoryData, isLoading: inventoryLoading } = useGetInventoryDetail(finalInventoryId || '', 'OPTIONS');
  const { data: optionsData, isLoading: optionsLoading } = useGetEquipmentOptions();
  const updateInventoryMutation = useUpdateInventory();

  useEffect(() => {
    if (inventoryData && inventoryData.options !== undefined) {
      setFormData(prev => ({
        ...prev,
        options: inventoryData.options || {},
        additional_options: inventoryData.additional_options || '',
        seller_notes: inventoryData.seller_notes || ''
      }));
    }
  }, [inventoryData]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Checkbox change handler
  const handleCheckboxChange = (type, key) => {
    if (!formData.options || !type || !key) {
      console.warn('Missing required data for checkbox change:', { formData: formData.options, type, key });
      return;
    }

    const updatedOptions = JSON.parse(JSON.stringify(formData.options));
    // Yanlış key'leri temizle
    Object.keys(updatedOptions[type] || {}).forEach(k => {
      if (k.toLowerCase() === key.toLowerCase() && k !== key) {
        delete updatedOptions[type][k];
      }
    });
    if (!updatedOptions[type]) updatedOptions[type] = {};
    if (!updatedOptions[type][key]) updatedOptions[type][key] = {};
    updatedOptions[type][key].is_selected = !updatedOptions[type][key].is_selected;

    // Local state'i güncelle
    setFormData(prev => ({
      ...prev,
      options: updatedOptions
    }));

    // API'ye güncelleme gönder (TSX'teki gibi)
    const finalId = finalInventoryId; // TSX'teki gibi direkt ID'yi kullan

    if (!finalId) {
      console.error("ID bulunamadı:", { inventoryId, urlId, finalInventoryId });
      return;
    }
    updateInventoryMutation.mutate({
      id: finalId,
      data: { options: updatedOptions }
    });
  };

  const renderOptions = optionsData?.results && Array.isArray(optionsData.results) ? optionsData.results : [];

  if (inventoryLoading || optionsLoading) {
    return (
      <div className="text-center p-4">
        <div className="spinner-border" role="status">
          <span className='sr-only'>{t('common.loading')}</span>
        </div>
      </div>
    );
  }

  return (
    <div className={classNames("options-tab", { "options-tab--theme2": isTheme2 })}>
      {isTheme2 ? <style>{theme2FieldsCss}</style> : null}
      <div className={classNames("row", { "options-tab-fields": isTheme2 })}>
      <div className="col-md-12 mb-4">
        <div className="card">
          <div className="card-header font-weight-bold text-uppercase">
            {t('optionsTab.equipmentAndVehicleOptions')}
          </div>
          <div className="card-body">
            <div className="row">
              {renderOptions.map((group) => (
                <div className="col-md-3 mb-4" key={group.type}>
                  <div className="form-group">
                    <label className="font-weight-bold text-uppercase">
                      {formatTypeTitle(group.type)}
                    </label>
                    <div
                      className="card"
                      style={{
                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.15), 0 2px 2px rgba(0, 0, 0, 0.1)',
                      }}
                    >
                      <div className="card-body" style={{ height: '250px', overflowY: 'auto', maxHeight: '300px' }}>
                        {group.inventory_options && Array.isArray(group.inventory_options) && group.inventory_options.length > 0 ? (
                          group.inventory_options.map((opt) => (
                            <FormGroupCheckbox check key={opt.value} className="mb-2">
                              <Label check>
                                <Input
                                  style={checkboxStyle}
                                  type="checkbox"
                                  checked={!!formData.options?.[group.type]?.[opt.value]?.is_selected}
                                  onChange={() => handleCheckboxChange(group.type, opt.value)}
                                  disabled={isDisabled}
                                />
                                <span className="form-check-sign" />
                                <span>
                                  {opt.name}
                                </span>
                              </Label>
                            </FormGroupCheckbox>
                          ))
                        ) : (
                          <div className="text-muted text-center py-3" style={{ fontSize: '0.875rem' }}>
                            {t('optionsTab.noOptionsAvailable')}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="row mt-4">
              <div className="col-md-6">
                <div className="form-group">
                  <label className="font-weight-bold text-uppercase">
                    {t('optionsTab.additionalOptions')}: <span style={{ fontWeight: 400, fontSize: '0.75rem' }}>({t('optionsTab.maxCharacters')})</span>
                    <span style={{ marginLeft: 8, fontSize: '0.75rem' }}>
                      {2000 - (formData.additional_options?.length || 0)} {t('optionsTab.remain')}
                    </span>
                  </label>
                  <textarea
                    style={inputStyle}
                    className="form-control"
                    rows={4}
                    maxLength={2000}
                    value={formData.additional_options}
                    onChange={(e) => handleInputChange('additional_options', e.target.value)}
                    disabled={isDisabled}
                  />
                </div>
              </div>
              <div className="col-md-6">
                <div className="form-group">
                  <label className="font-weight-bold text-uppercase">
                    {t('optionsTab.sellerNotes')}: <span style={{ fontWeight: 400, fontSize: '0.75rem' }}>({t('optionsTab.maxCharacters')})</span>
                    <span style={{ marginLeft: 8, fontSize: '0.75rem' }}>
                      {2000 - (formData.seller_notes?.length || 0)} {t('optionsTab.remain')}
                    </span>
                  </label>
                  <textarea
                    style={inputStyle}
                    className="form-control"
                    rows={4}
                    maxLength={2000}
                    value={formData.seller_notes}
                    onChange={(e) => handleInputChange('seller_notes', e.target.value)}
                    disabled={isDisabled}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
};

export default OptionsTab; 